/**
 * NotificationGate — Full-screen blocking overlay when push is not granted.
 *
 * Three states:
 *  1. "default" — soft prompt with "Enable Notifications" button
 *  2. "granted" — green bell jiggle → then reload
 *  3. "denied"  — red bell jiggle + manual instructions per platform/browser
 *
 * 2026 best-practices:
 *  - Never calls requestPermission() without a user gesture (button click)
 *  - Handles permanent "denied" state with browser-settings guidance
 *  - Scans 3× after enabling to confirm it actually stuck
 *  - Re-checks on visibility change so "I've Reset Settings" works instantly
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useAnimation } from 'motion/react';
import { Bell, Loader2, RefreshCw } from 'lucide-react';
import { MODAL_COLORS } from '@/components/modal';
import { platform } from '@/utils/platform';

interface NotificationGateProps {
  blocked: boolean;
  onEnable: () => Promise<boolean>;
  onCheck: () => Promise<boolean>;
}

type PermissionState = 'default' | 'granted' | 'denied';

function detectBrowser(): 'chrome' | 'firefox' | 'safari' | 'edge' | 'other' {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome') && !ua.includes('edg/')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  return 'other';
}

function getWebPermissionState(): PermissionState {
  if (!('Notification' in window)) return 'granted'; // not supported — don't block
  return Notification.permission as PermissionState;
}

export function NotificationGate({ blocked, onEnable, onCheck }: NotificationGateProps) {
  const { t } = useTranslation();
  const [permState, setPermState] = useState<PermissionState>(
    platform.isWeb ? getWebPermissionState() : 'default'
  );
  const [checking, setChecking] = useState(false);
  const [justGranted, setJustGranted] = useState(false);
  const bellControls = useAnimation();

  const jiggleBell = useCallback(() => {
    bellControls.start({
      rotate: [0, 20, -20, 15, -15, 10, -10, 5, -5, 0],
      transition: { duration: 0.6 },
    });
  }, [bellControls]);

  // Keep permState synced with actual browser state (e.g. after user resets in settings)
  const refreshState = useCallback(() => {
    if (platform.isWeb) {
      const state = getWebPermissionState();
      setPermState(state);
      if (state === 'granted') {
        setJustGranted(true);
      }
    } else if (platform.isNative) {
      onCheck().then((granted) => {
        setPermState(granted ? 'granted' : 'denied');
        if (granted) setJustGranted(true);
      });
    }
  }, [onCheck]);

  // Green bell jiggle + reload after granted
  useEffect(() => {
    if (!justGranted) return;
    jiggleBell();
    const timer = setTimeout(() => window.location.reload(), 1200);
    return () => clearTimeout(timer);
  }, [justGranted, jiggleBell]);

  // Jiggle bell on denied state
  useEffect(() => {
    if (permState === 'denied' && blocked) {
      jiggleBell();
    }
  }, [permState, blocked, jiggleBell]);

  useEffect(() => {
    if (!blocked) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [blocked, refreshState]);

  // Don't render the gate if not blocked (and not showing the success state)
  if (!blocked && !justGranted) return null;

  /** Scan permission 3× with 600ms gap. */
  const scanPermission = async (): Promise<boolean> => {
    for (let i = 0; i < 3; i++) {
      const granted = await onCheck();
      if (granted) return true;
      if (i < 2) await new Promise((r) => setTimeout(r, 600));
    }
    return false;
  };

  const handleEnable = async () => {
    setChecking(true);
    await onEnable();
    const granted = await scanPermission();
    setChecking(false);
    if (granted) {
      setPermState('granted');
      setJustGranted(true);
    } else {
      // Browser may have auto-denied or user clicked Block
      refreshState();
      jiggleBell();
    }
  };

  const handleRefresh = async () => {
    setChecking(true);
    const granted = await scanPermission();
    setChecking(false);
    if (granted) {
      setPermState('granted');
      setJustGranted(true);
    } else {
      refreshState();
      jiggleBell();
    }
  };

  const isDenied = permState === 'denied';
  const browser = platform.isWeb ? detectBrowser() : null;

  // Bell color: green when just granted, red when denied, blue when default
  const bellColor = justGranted ? '#16A34A' : isDenied ? '#DC2626' : MODAL_COLORS.header;
  const bellBg = justGranted ? '#F0FDF4' : isDenied ? '#FEF2F2' : '#EFF6FF';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full max-w-sm rounded-3xl p-6 space-y-5 text-center"
        style={{ backgroundColor: '#ffffff' }}
      >
        {/* Bell Icon */}
        <div
          className="mx-auto flex items-center justify-center rounded-full"
          style={{ width: 64, height: 64, backgroundColor: bellBg }}
        >
          <motion.div animate={bellControls}>
            <Bell size={32} style={{ color: bellColor }} />
          </motion.div>
        </div>

        {justGranted ? (
          <>
            <h2 className="text-xl font-extrabold" style={{ color: '#16A34A' }}>
              {t('onboarding.pushEnabled')}
            </h2>
            <Loader2 size={20} className="animate-spin mx-auto" style={{ color: '#16A34A' }} />
          </>
        ) : (
          <>
            {/* Title */}
            <h2 className="text-xl font-extrabold text-black">
              {isDenied ? t('pushGate.blockedTitle') : t('pushGate.title')}
            </h2>

            {/* Description */}
            <p className="text-sm" style={{ color: '#6B7280' }}>
              {isDenied ? t('pushGate.blockedDescription') : t('pushGate.description')}
            </p>

            {isDenied ? (
              <>
                {/* Platform-specific instructions */}
                <div
                  className="rounded-xl p-4 text-start space-y-2"
                  style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
                >
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#991B1B' }}>
                    {t('pushGate.howToFix')}
                  </p>
                  <PlatformInstructions browser={browser} />
                </div>

                {/* Check again button */}
                <button
                  onClick={handleRefresh}
                  disabled={checking}
                  className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{
                    backgroundColor: MODAL_COLORS.header,
                    color: '#ffffff',
                    border: `1px solid ${MODAL_COLORS.border}`,
                  }}
                >
                  {checking ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      {t('pushGate.iResetSettings')}
                    </>
                  )}
                </button>
              </>
            ) : (
              /* Default state — soft prompt */
              <button
                onClick={handleEnable}
                disabled={checking}
                className="w-full rounded-xl py-3.5 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-70 cursor-pointer flex items-center justify-center gap-2"
                style={{
                  backgroundColor: MODAL_COLORS.header,
                  color: '#ffffff',
                  border: `1px solid ${MODAL_COLORS.border}`,
                }}
              >
                {checking ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <Bell size={16} />
                    {t('pushGate.enableButton')}
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Platform/browser-specific instructions for resetting notification permissions. */
function PlatformInstructions({ browser }: { browser: string | null }) {
  const { t } = useTranslation();

  if (platform.isIOS) {
    return (
      <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#7F1D1D' }}>
        <li>{t('pushGate.ios.step1')}</li>
        <li>{t('pushGate.ios.step2')}</li>
        <li>{t('pushGate.ios.step3')}</li>
      </ol>
    );
  }

  if (platform.isAndroid) {
    return (
      <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#7F1D1D' }}>
        <li>{t('pushGate.android.step1')}</li>
        <li>{t('pushGate.android.step2')}</li>
        <li>{t('pushGate.android.step3')}</li>
      </ol>
    );
  }

  // Web — browser-specific
  if (browser === 'chrome' || browser === 'edge') {
    return (
      <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#7F1D1D' }}>
        <li>{t('pushGate.chrome.step1')}</li>
        <li>{t('pushGate.chrome.step2')}</li>
        <li>{t('pushGate.chrome.step3')}</li>
      </ol>
    );
  }

  if (browser === 'safari') {
    return (
      <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#7F1D1D' }}>
        <li>{t('pushGate.safari.step1')}</li>
        <li>{t('pushGate.safari.step2')}</li>
        <li>{t('pushGate.safari.step3')}</li>
      </ol>
    );
  }

  if (browser === 'firefox') {
    return (
      <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#7F1D1D' }}>
        <li>{t('pushGate.firefox.step1')}</li>
        <li>{t('pushGate.firefox.step2')}</li>
        <li>{t('pushGate.firefox.step3')}</li>
      </ol>
    );
  }

  // Generic fallback
  return (
    <p className="text-xs" style={{ color: '#7F1D1D' }}>
      {t('pushGate.generic')}
    </p>
  );
}
